import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { FormControlLabel, Grid, Radio, RadioGroup, TextField } from '@mui/material';
import { ProjectTypeEnum } from '~/api/types';
import { useGetProjectQuery, useGetUserDatasetsQuery } from '~/api/v2/hooks';
import { modalOverflowBoxStyles } from '~/components/styles';
import { useWbsWarningAlert } from '~/hooks';
import { useAppSelector } from '~/redux';
import { selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { MAX_DESCRIPTION_LENGTH } from '~/validation/projectSchema';
import { DatasetSelect, OperatingUnitIdSelect, ProjectTitleField, WbsCodeField } from '..';

const ProjectForm = () => {
  const projectId = +useParams().projectid!;
  const { data } = useGetProjectQuery({ projectId }, { skip: !projectId });
  const userId = useAppSelector(selectCurrentUserId) ?? '';
  const { data: userDestinationDatasets } = useGetUserDatasetsQuery(
    { userId, destinationType: true },
    { skip: !userId },
  );
  const wbsAlert = useWbsWarningAlert({ data, entityLabel: 'project' });
  const {
    control,
    register,
    formState: { defaultValues, errors },
  } = useFormContext();

  return (
    <Grid container spacing={4} alignItems="center" sx={modalOverflowBoxStyles}>
      {wbsAlert}
      <Grid size={{ xs: 12, md: 7 }}>
        <ProjectTitleField />
      </Grid>
      {!!userDestinationDatasets?.length && (
        <Grid size={12}>
          <FormControlLabel
            label="PROJECT TYPE"
            required
            sx={{ width: 'fit-content' }}
            control={
              <Controller
                name="type"
                control={control}
                disabled={!!projectId}
                render={({ field }) => (
                  <RadioGroup row {...field}>
                    {[ProjectTypeEnum.CONSUMER, ProjectTypeEnum.PRODUCER].map((type) => (
                      <FormControlLabel
                        key={type}
                        labelPlacement="end"
                        disabled={!!projectId}
                        value={type}
                        control={<Radio size="small" />}
                        label={type.charAt(0) + type.slice(1).toLowerCase()}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            }
          />
        </Grid>
      )}
      {defaultValues?.name && (
        <Grid size={12}>
          <FormControlLabel
            label="PROJECT STATUS"
            control={<TextField defaultValue={defaultValues?.status} disabled {...register('status')} />}
          />
        </Grid>
      )}
      <Grid size={12}>
        <FormControlLabel
          required
          label="DESCRIPTION"
          control={
            <TextField
              {...register('description')}
              multiline
              rows={3}
              helperText={errors.description?.message as string}
              error={!!errors.description}
              slotProps={{ htmlInput: { maxLength: MAX_DESCRIPTION_LENGTH } }}
            />
          }
        />
      </Grid>
      <Grid size={6}>
        <OperatingUnitIdSelect />
      </Grid>
      <Grid size={6}>
        <WbsCodeField />
      </Grid>
      <DatasetSelect />
    </Grid>
  );
};

export default ProjectForm;
